import React, { useState } from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { Comment, updateComment, deleteComment, hideComment, unhideComment } from '../../store/slices/commentsSlice';
import CommentReactions from './CommentReactions';
import CommentForm from './CommentForm';
import Button from '../UI/Button';
import UserInfo from '../UI/UserInfo';

interface CommentItemProps {
  comment: Comment;
  replies: Comment[];
  entityType: 'project' | 'projectFile';
  entityId: string;
  isProjectOwner?: boolean;
  depth?: number;
}

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  replies,
  entityType,
  entityId,
  isProjectOwner = false,
  depth = 0,
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [editRating, setEditRating] = useState<number | undefined>(comment.rating);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isOwner = user?.id === comment.userId;
  const maxDepth = 3;

  const handleEdit = () => {
    setEditContent(comment.content);
    setEditRating(comment.rating);
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!editContent.trim() || isUpdating) return;
    setIsUpdating(true);
    try {
      await dispatch(updateComment({ commentId: comment.id || comment._id || '', content: editContent.trim(), rating: editRating }));
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating comment:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setEditContent(comment.content);
    setEditRating(comment.rating);
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await dispatch(deleteComment(comment.id || comment._id || ''));
    } catch (error) {
      console.error('Error deleting comment:', error);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleHide = async () => {
    if (!isProjectOwner) return;
    try {
      if (comment.hidden) {
        await dispatch(unhideComment(comment.id || comment._id || ''));
      } else {
        await dispatch(hideComment({ commentId: comment.id || comment._id || '' }));
      }
    } catch (error) {
      console.error('Error hiding/unhiding comment:', error);
    }
  };

  // Don't show hidden comments to non-owners
  if (comment.hidden && !isProjectOwner) {
    return null;
  }

  const userInfo = typeof comment.userId === 'object' ? comment.userId : comment.userId;

  return (
    <div className={`${depth > 0 ? 'ml-8 border-l-2 border-[var(--color-neutral-border)] pl-4' : ''}`}>
      <div className={`bg-[var(--color-default-background)] border border-[var(--color-neutral-border)] rounded-lg p-4 ${comment.hidden ? 'opacity-60 bg-[var(--color-neutral-50)]' : ''}`}>
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <UserInfo user={userInfo} size="md" showBadges={true} showReputation={true} />
            <p className="text-xs text-[var(--color-subtext-color)]">
              {new Date(comment.createdAt).toLocaleString()}
              {comment.edited && <span className="ml-1 text-[var(--color-subtext-color)]">(edited)</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {comment.rating !== undefined && comment.rating !== null && (
              <div className="flex items-center gap-1 text-[var(--color-warning-500)]">
                {Array.from({ length: 6 }).map((_, i) => (
                  <span key={i} className={i < comment.rating! ? 'text-[var(--color-warning-500)]' : 'text-[var(--color-neutral-300)]'}>
                    ★
                  </span>
                ))}
                <span className="text-xs text-[var(--color-subtext-color)] ml-1">{comment.rating}/6</span>
              </div>
            )}
            {isOwner && (
              <span className="text-xs text-[var(--color-brand-primary)] font-medium">You</span>
            )}
            {comment.hidden && isProjectOwner && (
              <span className="text-xs text-[var(--color-warning-600)] font-medium">Hidden</span>
            )}
          </div>
        </div>

        {isEditing ? (
          <div className="mb-3">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--color-neutral-border)] rounded-md bg-[var(--color-default-background)] text-[var(--color-default-font)] focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-500)] mb-2"
              rows={4}
            />
            <div className="flex items-center gap-2 mb-2">
              <label className="text-sm font-medium text-[var(--color-default-font)]">Rating:</label>
              {[0, 1, 2, 3, 4, 5, 6].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setEditRating(star)}
                  className={`text-xl transition-colors ${
                    editRating !== undefined && editRating >= star
                      ? 'text-[var(--color-warning-500)]'
                      : 'text-[var(--color-neutral-300)] hover:text-[var(--color-warning-400)]'
                  }`}
                >
                  ★
                </button>
              ))}
              <button
                type="button"
                onClick={() => setEditRating(undefined)}
                className="text-sm text-[var(--color-subtext-color)] hover:text-[var(--color-default-font)] ml-2"
              >
                Clear
              </button>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSaveEdit} isLoading={isUpdating} disabled={!editContent.trim()}>
                Save
              </Button>
              <Button size="sm" variant="outline" onClick={handleCancelEdit} disabled={isUpdating}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-[var(--color-default-font)] mb-3 whitespace-pre-wrap">{comment.content}</p>

            <div className="flex items-center gap-4 mb-3">
              <CommentReactions
                commentId={comment.id || comment._id || ''}
                reactionSummary={comment.reactionSummary}
                userReactions={comment.userReactions}
              />
              {depth < maxDepth && user && (
                <button
                  onClick={() => setShowReplyForm(!showReplyForm)}
                  className="text-sm text-[var(--color-brand-primary)] hover:text-[var(--color-brand-700)]"
                >
                  Reply
                </button>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2 text-sm">
              {isOwner && (
                <>
                  <button
                    onClick={handleEdit}
                    className="text-[var(--color-brand-primary)] hover:text-[var(--color-brand-700)]"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="text-[var(--color-error-600)] hover:text-[var(--color-error-700)]"
                  >
                    Delete
                  </button>
                </>
              )}
              {isProjectOwner && (
                <button
                  onClick={handleHide}
                  className="text-[var(--color-warning-600)] hover:text-[var(--color-warning-700)]"
                >
                  {comment.hidden ? 'Unhide' : 'Hide'}
                </button>
              )}
            </div>

            {showDeleteConfirm && (
              <div className="mt-3 p-3 bg-[var(--color-error-50)] border border-[var(--color-error-200)] rounded-lg">
                <p className="text-sm text-[var(--color-error-800)] mb-2">Are you sure you want to delete this comment?</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="danger" onClick={handleDelete} isLoading={isDeleting}>
                    Delete
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {showReplyForm && (
          <div className="mt-4 pt-4 border-t">
            <CommentForm
              entityType={entityType}
              entityId={entityId}
              parentId={comment.id || comment._id}
              onSuccess={() => {
                setShowReplyForm(false);
              }}
            />
          </div>
        )}
      </div>

      {replies.length > 0 && (
        <div className="mt-4 space-y-4">
          {replies.map((reply) => (
            <CommentItem
              key={reply.id || reply._id}
              comment={reply}
              replies={[]}
              entityType={entityType}
              entityId={entityId}
              isProjectOwner={isProjectOwner}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentItem;
